import React, { useEffect, useState, useRef, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, EyeIcon, EyeOffIcon, MailIcon, LockIcon, CheckIcon } from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { useAuth } from './AuthProvider';
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode: 'signin' | 'signup';
  redirectTo?: string;
}
// ─── reCAPTCHA Feature Flag ───────────────────────────────────────────────────
// Set to true to re-enable reCAPTCHA verification on signup
const RECAPTCHA_ENABLED = false;
export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'signin',
  redirectTo
}) => {
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Forgot password state
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const {
    login,
    register,
    isAuthenticated,
    user
  } = useAuth();
  const navigate = useNavigate();
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const interactionStartRef = useRef<EventTarget | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const recaptchaWidgetIdRef = useRef<number | null>(null);
  // Safe access to reCAPTCHA site key with hardcoded fallback
  const recaptchaSiteKey = typeof import.meta !== 'undefined' && import.meta.env?.VITE_RECAPTCHA_SITE_KEY || '6Lco-I4sAAAAABZEWbBtmpZ6iCD96a5K_8yw2Kyj';
  // Load reCAPTCHA v2 script
  useEffect(() => {
    if (!recaptchaSiteKey || recaptchaSiteKey === 'YOUR_RECAPTCHA_SITE_KEY') return;
    if ((window as any).grecaptcha && (window as any).grecaptcha.render) {
      setRecaptchaReady(true);
      return;
    }
    ;
    (window as any).onRecaptchaLoad = () => {
      setRecaptchaReady(true);
    };
    if (!document.querySelector('script[src*="recaptcha/api.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);
  // Render reCAPTCHA widget when ready and in signup mode
  useEffect(() => {
    if (!recaptchaReady || mode !== 'signup' || !isOpen || !recaptchaContainerRef.current || !recaptchaSiteKey || recaptchaSiteKey === 'YOUR_RECAPTCHA_SITE_KEY') return;
    const grecaptcha = (window as any).grecaptcha;
    if (!grecaptcha || !grecaptcha.render) return;
    // Clear previous widget
    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.innerHTML = '';
    }
    recaptchaWidgetIdRef.current = null;
    try {
      const isDark = document.documentElement.classList.contains('dark');
      const widgetId = grecaptcha.render(recaptchaContainerRef.current, {
        sitekey: recaptchaSiteKey,
        callback: (token: string) => setRecaptchaToken(token),
        'expired-callback': () => setRecaptchaToken(null),
        theme: isDark ? 'dark' : 'light'
      });
      recaptchaWidgetIdRef.current = widgetId;
    } catch (err) {
      // reCAPTCHA render failed silently
    }
  }, [recaptchaReady, mode, isOpen]);
  // Password strength calculation
  const getPasswordStrength = (pw: string) => {
    if (!pw) return {
      level: 'none' as const,
      score: 0,
      label: ''
    };
    let score = 0;
    const checks = {
      minLength: pw.length >= 8,
      hasUpper: /[A-Z]/.test(pw),
      hasLower: /[a-z]/.test(pw),
      hasNumber: /[0-9]/.test(pw),
      hasSpecial: /[^A-Za-z0-9]/.test(pw)
    };
    if (checks.minLength) score++;
    if (checks.hasUpper) score++;
    if (checks.hasLower) score++;
    if (checks.hasNumber) score++;
    if (checks.hasSpecial) score++;
    let level: 'none' | 'weak' | 'medium' | 'strong' = 'weak';
    let label = 'Weak password';
    if (score >= 5) {
      level = 'strong';
      label = 'Strong password';
    } else if (score >= 3) {
      level = 'medium';
      label = 'Medium password';
    }
    return {
      level,
      score,
      label,
      checks
    };
  };
  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const isPasswordValid = passwordStrength.level === 'strong' || passwordStrength.level === 'medium';
  const isSignupReady = mode === 'signup' ? isPasswordValid && passwordsMatch && name.trim() !== '' && email.trim() !== '' && (!RECAPTCHA_ENABLED || recaptchaToken !== null) : true;
  // Reset form when modal opens or mode changes
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setName('');
      setError('');
      setIsSubmitting(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setRecaptchaToken(null);
      if (recaptchaWidgetIdRef.current !== null && (window as any).grecaptcha) {
        try {
          ;
          (window as any).grecaptcha.reset(recaptchaWidgetIdRef.current);
        } catch (e) {
          // Widget may not exist yet
        }
      }
    }
  }, [isOpen, initialMode]);
  // Handle redirect after successful authentication
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      onClose();
      // Only redirect to dashboard if explicitly requested
      if (user?.activeRole === 'seller' || user?.isSeller) {
        // Only redirect to seller dashboard if redirectTo is undefined or explicitly set to dashboard
        if (!redirectTo || redirectTo === 'dashboard') {
          navigate('/seller-dashboard');
        }
      }
      // Handle specific redirects if provided
      else if (redirectTo === 'submission-form') {
        // Redirect to the seller product page instead of scrolling to submission form
        navigate('/sell-product');
      } else if (redirectTo) {
        navigate(redirectTo);
      }
      // If no redirectTo is specified, just close the modal and stay on current page
    }
  }, [isAuthenticated, isOpen, redirectTo, navigate, onClose, user]);
  // Add a separate useEffect to handle redirection when user object changes
  // This ensures we catch role changes even if they happen after authentication
  useEffect(() => {
    if (isAuthenticated && user && isOpen) {
      if (user.activeRole === 'seller' || user.isSeller) {
        onClose();
        navigate('/seller-dashboard');
      }
    }
  }, [user, isAuthenticated, navigate, onClose, isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      // Basic validation
      if (!email.trim()) {
        throw new Error('Email is required');
      }
      if (!password.trim()) {
        throw new Error('Password is required');
      }
      if (mode === 'signup' && !name.trim()) {
        throw new Error('Name is required');
      }
      if (mode === 'signup') {
        if (!isPasswordValid) {
          throw new Error('Password must meet the strength requirements.');
        }
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match.');
        }
        // reCAPTCHA verification (temporarily disabled via RECAPTCHA_ENABLED flag)
        if (RECAPTCHA_ENABLED) {
          if (!recaptchaToken) {
            throw new Error('Please complete the reCAPTCHA verification.');
          }
          // Verify reCAPTCHA token with backend
          try {
            // Replace this URL with your actual Cloud Function endpoint
            const response = await fetch('https://us-central1-dgstack-eaec5.cloudfunctions.net/verifyRecaptcha', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                token: recaptchaToken
              })
            });
            if (!response.ok) {
              throw new Error('reCAPTCHA verification failed on server');
            }
            const data = await response.json();
            if (!data.success) {
              throw new Error('reCAPTCHA verification failed: ' + data.message);
            }
          } catch (error: any) {
            console.error('reCAPTCHA verification error:', error);
            throw new Error('Failed to verify reCAPTCHA. Please try again.');
          }
        }
      }
      if (mode === 'signin') {
        // Sign in with Firebase Auth
        await login(email, password, rememberMe);
      } else {
        // Sign up with Firebase Auth
        await register({
          name: name,
          email: email,
          roles: ['buyer', 'seller'],
          activeRole: 'seller'
        }, password);
      }
    } catch (err: any) {
      console.error('Authentication error:', err);
      // Handle Firebase Auth errors
      if (err.code) {
        switch (err.code) {
          // Sign-up specific errors (safe to distinguish)
          case 'auth/email-already-in-use':
            setError('This email is already in use. Please try logging in instead.');
            break;
          case 'auth/weak-password':
            setError('Password is too weak. Please use at least 6 characters.');
            break;
          // Non-credential errors (safe to distinguish)
          case 'auth/too-many-requests':
            setError('Too many failed login attempts. Please try again later.');
            break;
          case 'auth/network-request-failed':
            setError('Network error. Please check your internet connection and try again.');
            break;
          // All credential/login errors — single generic message
          // Covers: auth/user-not-found, auth/wrong-password, auth/invalid-credential,
          // auth/invalid-email, auth/user-disabled, and any other auth error
          default:
            setError('Invalid email or password. Please try again.');
        }
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } finally {
      // Always set isSubmitting to false, regardless of success or failure
      setIsSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" onMouseDown={e => {
    interactionStartRef.current = e.target;
  }} onTouchStart={e => {
    interactionStartRef.current = e.target;
  }} onClick={e => {
    const startedOutside = interactionStartRef.current && contentRef.current && !contentRef.current.contains(interactionStartRef.current as Node);
    const endedOutside = contentRef.current && !contentRef.current.contains(e.target as Node);
    if (startedOutside && endedOutside) {
      onClose();
    }
    interactionStartRef.current = null;
  }} data-id="element-102">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-103">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity pointer-events-none" aria-hidden="true" data-id="element-104"></div>
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle pointer-events-none" aria-hidden="true" data-id="element-105">
          &#8203;
        </span>
        <div ref={contentRef} className="relative z-10 w-full sm:inline-block align-bottom bg-white dark:bg-[#162825] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()} data-id="element-106">
          <div className="absolute top-0 right-0 pt-4 pr-4" data-id="element-107">
            <button type="button" className="bg-white dark:bg-transparent rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none" onClick={onClose} data-id="element-108">
              <span className="sr-only" data-id="element-109">Close</span>
              <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-110" />
            </button>
          </div>
          <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-111">
            <div className="sm:flex sm:items-start" data-id="element-112">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full" data-id="element-113">
                <h3 className="text-2xl leading-6 font-semibold text-gray-900 dark:text-white mb-6" id="modal-title" data-id="element-114">
                  {mode === 'signin' ? 'Sign In to Your Account' : mode === 'signup' ? 'Create Your Account' : 'Reset Your Password'}
                </h3>
                {mode === 'forgot' ? <div data-id="element-115">
                    {resetSuccess ? <div className="mb-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-md text-sm" data-id="element-116">
                        <div className="flex items-center gap-2 mb-1" data-id="element-117">
                          <CheckIcon className="h-4 w-4" data-id="element-118" />
                          <span className="font-medium" data-id="element-119">Email sent</span>
                        </div>
                        Password reset email sent. Please check your inbox.
                      </div> : <>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-120">
                          Enter your email address and we'll send you a link to
                          reset your password.
                        </p>
                        {resetError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm" data-id="element-121">
                            {resetError}
                          </div>}
                        <form onSubmit={async e => {
                    e.preventDefault();
                    setResetError('');
                    const trimmed = resetEmail.trim();
                    if (!trimmed) {
                      setResetError('Please enter your email address.');
                      return;
                    }
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
                      setResetError('Please enter a valid email address.');
                      return;
                    }
                    setIsResetting(true);
                    try {
                      await sendPasswordResetEmail(auth, trimmed);
                      setResetSuccess(true);
                    } catch {
                      setResetError('Unable to send reset email. Please check your email and try again.');
                    } finally {
                      setIsResetting(false);
                    }
                  }} className="space-y-4" data-id="element-122">
                          <div data-id="element-123">
                            <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-124">
                              Email Address
                            </label>
                            <div className="relative" data-id="element-125">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-126">
                                <MailIcon className="h-5 w-5 text-gray-400" data-id="element-127" />
                              </div>
                              <input type="email" id="reset-email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-white" placeholder="you@example.com" autoFocus data-id="element-128" />
                            </div>
                          </div>
                          <button type="submit" disabled={isResetting} className={`w-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-lg text-base font-medium hover:shadow-lg transition-all ${isResetting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-129">
                            {isResetting ? <span className="flex items-center justify-center" data-id="element-130">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-131">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-132"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-133"></path>
                                </svg>
                                Sending...
                              </span> : 'Send Reset Link'}
                          </button>
                        </form>
                      </>}
                    <div className="mt-6 text-center" data-id="element-134">
                      <button type="button" onClick={() => {
                    setResetSuccess(false);
                    setResetError('');
                    setMode('signin');
                  }} className="text-sm font-medium text-[#2DB489] dark:text-[#4ED1A0] hover:text-[#0A3C52] dark:hover:text-[#6FEEC0]" data-id="element-135">
                        ← Back to Sign In
                      </button>
                    </div>
                  </div> : <>
                    {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm" data-id="element-136">
                        {error}
                      </div>}
                    <form onSubmit={handleSubmit} className="space-y-4" data-id="element-137">
                      {mode === 'signup' && <div data-id="element-138">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-139">
                            Full Name
                          </label>
                          <input type="text" id="name" name="name" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-white" placeholder="Your name" data-id="element-140" />
                        </div>}
                      <div data-id="element-141">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-142">
                          Email
                        </label>
                        <div className="relative" data-id="element-143">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-144">
                            <MailIcon className="h-5 w-5 text-gray-400" data-id="element-145" />
                          </div>
                          <input type="email" id="email" name="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-white" placeholder="your@email.com" data-id="element-146" />
                        </div>
                      </div>
                      <div data-id="element-147">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-148">
                          Password
                        </label>
                        <div className="relative" data-id="element-149">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-150">
                            <LockIcon className="h-5 w-5 text-gray-400" data-id="element-151" />
                          </div>
                          <input type={showPassword ? 'text' : 'password'} id="password" name="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-white" placeholder={mode === 'signup' ? 'Create a password' : 'Your password'} data-id="element-152" />
                          <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} data-id="element-153">
                            {showPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" data-id="element-154" /> : <EyeIcon className="h-5 w-5 text-gray-400" data-id="element-155" />}
                          </button>
                        </div>
                        {/* Password Strength Indicator — signup only */}
                        {mode === 'signup' && password.length > 0 && <div className="mt-2.5 space-y-2" data-id="element-156">
                            {/* Strength bar */}
                            <div className="flex gap-1" data-id="element-157">
                              {[1, 2, 3, 4, 5].map(segment => <div key={segment} className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${passwordStrength.score >= segment ? passwordStrength.level === 'strong' ? 'bg-emerald-500' : passwordStrength.level === 'medium' ? 'bg-amber-500' : 'bg-red-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-158" />)}
                            </div>
                            {/* Strength label */}
                            <p className={`text-xs font-medium ${passwordStrength.level === 'strong' ? 'text-emerald-600 dark:text-emerald-400' : passwordStrength.level === 'medium' ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-159">
                              {passwordStrength.label}
                            </p>
                            {/* Requirement checklist */}
                            {passwordStrength.checks && <div className="grid grid-cols-2 gap-x-3 gap-y-0.5" data-id="element-160">
                                {[{
                          key: 'minLength',
                          label: '8+ characters'
                        }, {
                          key: 'hasUpper',
                          label: 'Uppercase letter'
                        }, {
                          key: 'hasLower',
                          label: 'Lowercase letter'
                        }, {
                          key: 'hasNumber',
                          label: 'Number'
                        }, {
                          key: 'hasSpecial',
                          label: 'Special character'
                        }].map(({
                          key,
                          label
                        }) => <span key={key} className={`text-xs flex items-center gap-1 ${(passwordStrength.checks as any)[key] ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-161">
                                    <CheckIcon className={`h-3 w-3 ${(passwordStrength.checks as any)[key] ? 'opacity-100' : 'opacity-30'}`} data-id="element-162" />
                                    {label}
                                  </span>)}
                              </div>}
                          </div>}
                      </div>
                      {/* Confirm Password — signup only */}
                      {mode === 'signup' && <div data-id="element-163">
                          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-164">
                            Confirm Password
                          </label>
                          <div className="relative" data-id="element-165">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-166">
                              <LockIcon className="h-5 w-5 text-gray-400" data-id="element-167" />
                            </div>
                            <input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:border-transparent dark:bg-[#0F1F1D] dark:text-white ${confirmPassword.length === 0 ? 'border-gray-300 dark:border-gray-600 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]' : passwordsMatch ? 'border-emerald-400 dark:border-emerald-500 focus:ring-emerald-500' : 'border-red-400 dark:border-red-500 focus:ring-red-500'}`} placeholder="Confirm your password" data-id="element-168" />
                            <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-169">
                              {showConfirmPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" data-id="element-170" /> : <EyeIcon className="h-5 w-5 text-gray-400" data-id="element-171" />}
                            </button>
                          </div>
                          {confirmPassword.length > 0 && !passwordsMatch && <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-id="element-172">
                              Passwords do not match
                            </p>}
                          {passwordsMatch && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1" data-id="element-173">
                              <CheckIcon className="h-3 w-3" data-id="element-174" />
                              Passwords match
                            </p>}
                        </div>}
                      {mode === 'signin' && <div className="flex items-center justify-between" data-id="element-175">
                          <div className="flex items-center" data-id="element-176">
                            <input id="remember-me" name="remember-me" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-gray-600 rounded" data-id="element-177" />
                            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-178">
                              Remember me
                            </label>
                          </div>
                          <div className="text-sm" data-id="element-179">
                            <button type="button" onClick={() => {
                        setResetEmail(email);
                        setResetSuccess(false);
                        setResetError('');
                        setMode('forgot');
                      }} className="font-medium text-[#2DB489] dark:text-[#4ED1A0] hover:text-[#0A3C52] dark:hover:text-[#6FEEC0]" data-id="element-180">
                              Forgot password?
                            </button>
                          </div>
                        </div>}
                      {RECAPTCHA_ENABLED && mode === 'signup' && <div className="flex flex-col items-center mt-4 mb-2" data-id="element-181">
                          <div ref={recaptchaContainerRef} data-id="element-182"></div>
                          {!recaptchaToken && recaptchaReady && <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center" data-id="element-183">
                              Please verify you are not a robot
                            </p>}
                        </div>}
                      <div className="mt-6" data-id="element-184">
                        <button type="submit" disabled={isSubmitting || !isSignupReady} className={`w-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-lg text-base font-medium hover:shadow-lg transition-all ${isSubmitting || !isSignupReady ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-185">
                          {isSubmitting ? <span className="flex items-center justify-center" data-id="element-186">
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-187">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-188"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-189"></path>
                              </svg>
                              {mode === 'signin' ? 'Signing in...' : 'Creating account...'}
                            </span> : mode === 'signin' ? 'Sign In' : 'Create Account'}
                        </button>
                      </div>
                    </form>
                    <div className="mt-6 text-center" data-id="element-190">
                      <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-191">
                        {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
                        <button type="button" onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')} className="font-medium text-[#2DB489] dark:text-[#4ED1A0] hover:text-[#0A3C52] dark:hover:text-[#6FEEC0]" data-id="element-192">
                          {mode === 'signin' ? 'Sign up' : 'Sign in'}
                        </button>
                      </p>
                    </div>
                  </>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};